import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { Button } from '../../../basic/Button';
import Typography from '../../../basic/Typography';
import { useAuth } from '../context/AuthContext';
import { getPortalSession, getTopUpSession } from '../services/authApi';
import { trackAuthEvent } from '../services/authTelemetry';
import type { ApiKeyRecord } from '../types';
import { formatCreditsAsUsd } from '../utils/credits';
import { AuthLayout } from './AuthLayout';
import formStyles from './AuthForm.module.css';
import styles from './AccountPage.module.css';

async function tryCopy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function AccountPage() {
  const {
    status,
    me,
    error,
    refreshMe,
    listApiKeys,
    createApiKey,
    revokeApiKeyById,
    logout,
  } = useAuth();

  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keysError, setKeysError] = useState<string | null>(null);

  const [newKey, setNewKey] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const balanceText = useMemo(() => formatCreditsAsUsd(me?.balance ?? null), [me?.balance]);

  const loadKeys = useCallback(async () => {
    setKeysLoading(true);
    setKeysError(null);
    try {
      const items = await listApiKeys();
      setKeys(items);
    } catch (err) {
      setKeysError(err instanceof Error ? err.message : 'failed_to_load_api_keys');
    } finally {
      setKeysLoading(false);
    }
  }, [listApiKeys]);

  const onCreateKey = useCallback(async () => {
    setCopyStatus(null);
    setKeysError(null);
    try {
      const created = await createApiKey();
      setNewKey(created.api_key);
      await loadKeys();
    } catch (err) {
      setKeysError(err instanceof Error ? err.message : 'failed_to_create_api_key');
    }
  }, [createApiKey, loadKeys]);

  const onRevokeKey = useCallback(
    async (id: number) => {
      const ok = window.confirm('Revoke this API key? This cannot be undone.');
      if (!ok) return;
      setKeysError(null);
      try {
        await revokeApiKeyById(id);
        await loadKeys();
      } catch (err) {
        setKeysError(err instanceof Error ? err.message : 'failed_to_revoke_api_key');
      }
    },
    [revokeApiKeyById, loadKeys],
  );

  const onCopyNewKey = useCallback(async () => {
    if (!newKey) return;
    const ok = await tryCopy(newKey);
    setCopyStatus(ok ? 'Copied to clipboard.' : 'Copy failed.');
  }, [newKey]);

  const onTopUp = useCallback(async (credits = 10000) => {
    setTopUpLoading(true);
    trackAuthEvent({ name: 'top_up_clicked', credits });
    try {
      const returnUrl = `${window.location.origin}/game/auth/account`;
      const resp = await getTopUpSession(returnUrl, credits);
      window.location.href = resp.url;
    } catch (err) {
      console.error('Failed to create top-up session:', err);
    } finally {
      setTopUpLoading(false);
    }
  }, []);

  const onManageBilling = useCallback(async () => {
    setPortalLoading(true);
    setPortalError(null);
    trackAuthEvent({ name: 'manage_billing_clicked' });
    try {
      const returnUrl = `${window.location.origin}/game/auth/account`;
      const resp = await getPortalSession(returnUrl);
      window.location.href = resp.url;
    } catch (err: any) {
      if (err.payload?.error === 'no_billing_account') {
        setPortalError('No billing account yet. Please top up first.');
      } else {
        setPortalError('Failed to load billing portal.');
      }
      console.error('Failed to create portal session:', err);
    } finally {
      setPortalLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status !== 'authenticated') return;
    void refreshMe();
    void loadKeys();
  }, [status, refreshMe, loadKeys]);

  useEffect(() => {
    if (me?.low_balance && me.balance !== null) {
      trackAuthEvent({ name: 'low_balance_shown', balance: me.balance });
    }
  }, [me?.low_balance, me?.balance]);

  if (status === 'anonymous') return <Navigate to="/auth/login" replace />;

  return (
    <AuthLayout title="Account">
      {error && (
        <div className={formStyles.error} role="alert">
          <Typography variant="body2">{error}</Typography>
        </div>
      )}

      <div className={styles.section}>
        <Typography variant="h4" gutterBottom>
          Profile
        </Typography>

        <div className={styles.kvGrid}>
          <div className={styles.kvRow}>
            <Typography variant="body2" color="secondary">
              Name
            </Typography>
            <Typography variant="body2">{me?.name ?? '—'}</Typography>
          </div>
          <div className={styles.kvRow}>
            <Typography variant="body2" color="secondary">
              Email
            </Typography>
            <Typography variant="body2">{me?.email ?? '—'}</Typography>
          </div>
          <div className={styles.kvRow}>
            <Typography variant="body2" color="secondary">
              User ID
            </Typography>
            <Typography variant="body2">{me?.sub ?? '—'}</Typography>
          </div>
          <div className={styles.kvRow}>
            <Typography variant="body2" color="secondary">
              Balance
            </Typography>
            <Typography variant="body2">{balanceText}</Typography>
          </div>
        </div>

        {me?.low_balance && (
          <div className={styles.lowBalanceBanner}>
            <div className={styles.bannerContent}>
              <Typography variant="body2">
                Low balance. Top up to avoid interruptions.
              </Typography>
            </div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => void onTopUp()}
              disabled={topUpLoading}
            >
              {topUpLoading ? 'Loading...' : 'Top up now'}
            </Button>
          </div>
        )}

        <div className={styles.actionsRow}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={() => void onTopUp(10000)}
            disabled={topUpLoading}
          >
            {topUpLoading ? 'Loading...' : 'Add $10 Credits'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="medium"
            onClick={() => void onManageBilling()}
            disabled={portalLoading}
          >
            {portalLoading ? 'Loading...' : 'Manage Billing'}
          </Button>
          <Button variant="text" color="secondary" size="medium" onClick={() => void logout()}>
            Log out
          </Button>
        </div>
        {portalError && (
          <Typography
            variant="caption"
            style={{ color: '#d32f2f', marginTop: 8, display: 'block' }}
          >
            {portalError}
          </Typography>
        )}
      </div>

      <div className={styles.section}>
        <Typography variant="h4" gutterBottom>
          API keys
        </Typography>

        <Typography variant="body2" color="secondary">
          Create an API key for programmatic access. Keep it secret; you'll only see the full value once.
        </Typography>

        <div className={styles.actionsRow}>
          <Button variant="contained" color="primary" size="medium" onClick={() => void onCreateKey()}>
            Create API key
          </Button>
        </div>

        {newKey && (
          <div className={styles.newKeyBox}>
            <Typography variant="body2" gutterBottom>
              New API key
            </Typography>
            <code className={styles.code}>{newKey}</code>
            <div className={styles.actionsRow}>
              <Button variant="outlined" color="primary" size="small" onClick={() => void onCopyNewKey()}>
                Copy
              </Button>
              <Button variant="text" color="secondary" size="small" onClick={() => setNewKey(null)}>
                Dismiss
              </Button>
            </div>
            {copyStatus && (
              <Typography variant="caption" color="secondary">
                {copyStatus}
              </Typography>
            )}
          </div>
        )}

        {keysError && (
          <div className={formStyles.error} role="alert">
            <Typography variant="body2">{keysError}</Typography>
          </div>
        )}

        {keysLoading ? (
          <div className={styles.loadingRow}>
            <div className="loading-spinner" />
            <Typography variant="body2" color="secondary">
              Loading…
            </Typography>
          </div>
        ) : (
          <div className={styles.table}>
            {keys.length === 0 ? (
              <Typography variant="body2" color="secondary">
                No API keys yet.
              </Typography>
            ) : (
              keys.map((k) => (
                <div key={k.id} className={styles.tableRow}>
                  <div className={styles.tableCell}>
                    <Typography variant="body2">#{k.id}</Typography>
                    <Typography variant="caption" color="secondary">
                      {new Date(k.createdAt).toLocaleString()}
                    </Typography>
                  </div>
                  <div className={styles.tableCell}>
                    <Typography variant="body2" color="secondary">
                      {k.scope.join(' ')}
                    </Typography>
                    {k.revoked && (
                      <Typography variant="caption" color="tertiary">
                        revoked
                      </Typography>
                    )}
                  </div>
                  <div className={styles.tableCellRight}>
                    {k.revoked ? (
                      <Button variant="text" color="secondary" size="small" disabled>
                        Revoked
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => void onRevokeKey(k.id)}
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className={styles.footerLinks}>
          <Link to="/auth/login">
            <Typography variant="caption" component="span" color="secondary">
              Switch account
            </Typography>
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}
